import { Controller } from '@hotwired/stimulus';
import { isMobileViewport } from '../../../utils/customer/is_mobile_viewport';

/**
 * 一番上に戻るボタンのコントローラー
 * スクロール位置に応じてボタンの表示・非表示を制御し、クリック時に一番上にスクロールする
 */
export default class extends Controller {
  static override targets = ['button'];

  static override values = {
    visibleThreshold: { type: Number, default: 300 },
    smoothScroll: { type: Boolean, default: true },
    // 画面下からこの距離 (px) に達したら位置を変更する
    bottomChangeThreshold: { type: Number, default: 200 },
    // 位置を変更する対象のパス (正規表現)
    bottomChangePathRegex: { type: String, default: '/ticket_groups/\\d+/confirmation/?$' },
    // 通常時の bottom クラス
    bottomClassDefault: { type: String, default: 'bottom-8' },
    // 画面下部に近い時の bottom クラス
    bottomClassNearBottom: { type: String, default: 'bottom-38' },
  };

  declare readonly buttonTarget: HTMLElement;
  declare readonly visibleThresholdValue: number;
  declare readonly smoothScrollValue: boolean;
  declare readonly bottomChangeThresholdValue: number;
  declare readonly bottomChangePathRegexValue: string;
  declare readonly bottomClassDefaultValue: string;
  declare readonly bottomClassNearBottomValue: string;

  private isBottomChangePage: boolean = false;
  private pathRegex: RegExp | null = null;

  override connect() {
    try {
      this.pathRegex = new RegExp(this.bottomChangePathRegexValue);
      this.isBottomChangePage = this.pathRegex.test(window.location.pathname);
    } catch {
      this.isBottomChangePage = false;
    }

    this.setupScrollListener();
  }

  override disconnect() {
    this.removeScrollListener();
  }

  /**
   * スクロールイベントリスナーを設定
   */
  private setupScrollListener() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // 初期状態を設定
    this.handleScroll();
  }

  /**
   * スクロールイベントリスナーを削除
   */
  private removeScrollListener() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * スクロールイベントのハンドラー
   */
  private handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.visibleThresholdValue) {
      this.showButton();
    } else {
      this.hideButton();
    }

    // 特定ページでのみ bottom 位置を変更
    if (this.isBottomChangePage && isMobileViewport()) {
      const scrollHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;

      const isNearBottom =
        scrollTop + windowHeight >= scrollHeight - this.bottomChangeThresholdValue;

      if (isNearBottom) {
        this.buttonTarget.classList.remove(this.bottomClassDefaultValue);
        this.buttonTarget.classList.add(this.bottomClassNearBottomValue);
      } else {
        // 通常: bottom-8
        this.buttonTarget.classList.add(this.bottomClassDefaultValue);
        this.buttonTarget.classList.remove(this.bottomClassNearBottomValue);
      }
    } else {
      // 対象ページ以外はデフォルト
      if (!this.buttonTarget.classList.contains(this.bottomClassDefaultValue)) {
        this.buttonTarget.classList.add(this.bottomClassDefaultValue);
      }
      if (this.buttonTarget.classList.contains(this.bottomClassNearBottomValue)) {
        this.buttonTarget.classList.remove(this.bottomClassNearBottomValue);
      }
    }
  }

  /**
   * ボタンを表示
   */
  private showButton() {
    this.buttonTarget.classList.remove('opacity-0', 'pointer-events-none');
    this.buttonTarget.classList.add('opacity-100');
  }

  /**
   * ボタンを非表示
   */
  private hideButton() {
    this.buttonTarget.classList.add('opacity-0', 'pointer-events-none');
    this.buttonTarget.classList.remove('opacity-100');
  }

  /**
   * 一番上にスクロールする
   */
  scrollToTop() {
    if (this.smoothScrollValue) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo(0, 0);
    }
  }
}
